import { Component, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Button, Group } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';

interface Props {
  children: ReactNode;
  /** Clears a caught error when it changes, without remounting the page. */
  resetKey?: string;
}

interface State {
  error: Error | null;
}

export class BetaErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  componentDidCatch(error: Error) {
    console.error('Beta page render error:', error);
  }

  private reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      return (
        <div
          className="beta-empty beta-empty-lg"
          role="alert"
          style={{ maxWidth: 520, margin: '48px auto', textAlign: 'center' }}
        >
          <IconAlertTriangle size={40} style={{ color: 'var(--beta-red-7)', marginBottom: 12 }} />
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--beta-text)', marginBottom: 6 }}>
            Something went wrong
          </div>
          <div style={{ marginBottom: 18 }}>
            This page couldn’t be displayed. Try again, or head back to the homepage.
          </div>
          <Group gap={10}>
            <Button variant="filled" onClick={this.reset}>
              Try again
            </Button>
            <Button component={Link} to="/" variant="default">
              Go home
            </Button>
          </Group>
        </div>
      );
    }
    return this.props.children;
  }
}
